import React, { useState } from "react";
import { motion } from "framer-motion";
import { Settings, Save, Upload, Globe, Shield, RefreshCw } from "lucide-react";

const GeneralSettings = () => {
  const [settings, setSettings] = useState({
    instituteName: "Setu Institute",
    website: "https://setuinstitute.com",
    email: "admin@setu.com",
    phone: "+91 9876543210",
    address: "123, Education Hub, Tech City",
    theme: "dark",
    language: "English",
  });

  const handleChange = (e) => {
    setSettings({ ...settings, [e.target.name]: e.target.value });
  };

  const handleSave = (e) => {
    e.preventDefault();
    alert("Settings saved successfully!");
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <motion.div
        className="max-w-4xl mx-auto space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-800 pb-6">
          <div className="p-3 bg-gradient-to-br from-gray-600 to-slate-600 rounded-2xl shadow-lg shadow-gray-500/20">
            <Settings className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">General Settings</h1>
            <p className="text-gray-400 mt-1">
              Configure your application and institute details
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSave}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {/* Left Column - Profile */}
          <div className="md:col-span-1 space-y-6">
            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700 text-center">
              <div className="w-24 h-24 mx-auto bg-gray-700 rounded-full flex items-center justify-center mb-4 relative group cursor-pointer overflow-hidden">
                <Upload className="w-8 h-8 text-gray-400 group-hover:scale-110 transition-transform" />
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-xs text-white">Change Logo</span>
                </div>
              </div>
              <h3 className="text-white font-medium">Institute Logo</h3>
              <p className="text-gray-500 text-xs mt-1">
                Recommended: 500x500px
              </p>
            </div>

            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700 space-y-4">
              <h3 className="text-gray-300 font-semibold flex items-center gap-2">
                <Shield className="w-4 h-4" /> Security
              </h3>
              <button
                type="button"
                className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm transition-colors"
              >
                Change Password
              </button>
              <button
                type="button"
                className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm transition-colors"
              >
                Two-Factor Auth
              </button>
            </div>
          </div>

          {/* Right Column - Form */}
          <div className="md:col-span-2 space-y-6">
            {/* Institute Details */}
            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Institute Details
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">
                    Institute Name
                  </label>
                  <input
                    type="text"
                    name="instituteName"
                    value={settings.instituteName}
                    onChange={handleChange}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={settings.email}
                      onChange={handleChange}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">
                      Phone
                    </label>
                    <input
                      type="text"
                      name="phone"
                      value={settings.phone}
                      onChange={handleChange}
                      className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">
                    Address
                  </label>
                  <textarea
                    name="address"
                    value={settings.address}
                    onChange={handleChange}
                    rows="3"
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                  ></textarea>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2 flex items-center gap-2">
                    <Globe className="w-3 h-3" /> Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={settings.website}
                    onChange={handleChange}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-gray-800 p-6 rounded-2xl border border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Application Preferences
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">
                    Language
                  </label>
                  <select
                    name="language"
                    value={settings.language}
                    onChange={handleChange}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                  >
                    <option>English</option>
                    <option>Hindi</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">
                    Theme
                  </label>
                  <select
                    name="theme"
                    value={settings.theme}
                    onChange={handleChange}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="dark">Dark Mode</option>
                    <option value="light">Light Mode</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-4">
              <button
                type="button"
                className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold shadow-lg shadow-blue-500/20"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default GeneralSettings;
